import { useRef } from 'react';
import { useI18n, type MessageKey } from '../i18n';
import { FILE_ACCEPT, type FileProblem } from '../lib/intake';
import { GarmentIcon, Icon, type GarmentIconName } from '../ui/Icon';

export interface IntakeActions {
  /** Reads the clipboard when allowed, else focuses the editor with a Ctrl+V hint. */
  onPaste: () => void;
  /** A file picked on this device (read in the browser, never sent). */
  onFile: (file: File) => void;
  /** Loads the starter in the interface language. */
  onStarter: () => void;
}

/**
 * « Votre patron » at the top of the welcome: three ways to start with her own pattern, and the
 * promise that the text stays in the browser.
 */
export function IntakeBlock({
  actions,
  ready = true,
  variant = 'welcome',
}: {
  actions: IntakeActions;
  ready?: boolean;
  /** `welcome`: the block of the welcome panel, with its title; `menu`: the same three entries inside the header's « New pattern » menu. */
  variant?: 'welcome' | 'menu';
}) {
  const { t } = useI18n();
  const fileInput = useRef<HTMLInputElement>(null);
  const headingId = variant === 'menu' ? 'header-intake' : 'welcome-intake';
  const items: Array<{ id: string; icon: GarmentIconName; title: string; hint: string; onClick: () => void }> = [
    { id: 'paste', icon: 'clipboard', title: t('intake.paste'), hint: t('intake.pasteHint'), onClick: actions.onPaste },
    { id: 'file', icon: 'folder', title: t('intake.file'), hint: t('intake.fileHint'), onClick: () => fileInput.current?.click() },
    { id: 'starter', icon: 'pencil', title: t('intake.starter'), hint: t('intake.starterHint'), onClick: actions.onStarter },
  ];
  return (
    <section aria-labelledby={headingId} className={variant === 'menu' ? '' : 'mt-8'} data-testid={variant === 'menu' ? 'intake-menu' : 'intake'}>
      <h3 id={headingId} className={variant === 'menu' ? 'sr-only' : 'font-display text-xl font-[440] text-fg'}>
        {t('intake.title')}
      </h3>
      <ul className={variant === 'menu' ? 'grid grid-cols-1 gap-2' : 'mt-4 grid grid-cols-1 gap-3'}>
        {items.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              onClick={item.onClick}
              disabled={!ready}
              data-testid={variant === 'menu' ? `new-${item.id}` : `intake-${item.id}`}
              className="group flex w-full items-center gap-4 rounded-card bg-surface px-4 py-3.5 text-left transition-colors hover:bg-editor hover:ring-1 hover:ring-inset hover:ring-line-strong disabled:cursor-wait sm:px-5"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-accent/[0.1] text-accent-strong">
                <GarmentIcon name={item.icon} size={22} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="font-display block text-md font-[500] leading-snug text-fg">{item.title}</span>
                <span className="mt-0.5 block text-xs leading-relaxed text-muted">{item.hint}</span>
              </span>
              <Icon name="arrow" size={14} className="text-subtle transition-colors group-hover:text-accent-strong" />
            </button>
          </li>
        ))}
      </ul>
      <p className="mt-3 flex items-center gap-1.5 text-xs text-muted" data-testid="intake-private">
        <Icon name="lock" size={11} className="text-accent-strong" />
        {t('intake.private')}
      </p>
      <input
        ref={fileInput}
        type="file"
        accept={FILE_ACCEPT}
        hidden
        data-testid={variant === 'menu' ? 'new-file-input' : 'intake-file-input'}
        onChange={(e) => {
          const file = e.currentTarget.files?.[0];
          // Cleared at once, so that picking the same file again still reads it.
          e.currentTarget.value = '';
          if (file) actions.onFile(file);
        }}
      />
    </section>
  );
}

export type IntakeHintKind = 'paste' | 'pasteTouch' | 'clipboardEmpty' | FileProblem;

export const INTAKE_HINT_KEYS: Record<IntakeHintKind, MessageKey> = {
  paste: 'intake.hint.paste',
  pasteTouch: 'intake.hint.pasteTouch',
  clipboardEmpty: 'intake.hint.clipboardEmpty',
  pdf: 'intake.hint.pdf',
  unsupported: 'intake.hint.unsupported',
  tooBig: 'intake.hint.tooBig',
  empty: 'intake.hint.empty',
  readFailed: 'intake.hint.readFailed',
};

/** A short line above the editor: where to paste, or why a file was not loaded (a PDF, a .docx…). */
export function IntakeHint({ kind, onDismiss }: { kind: IntakeHintKind; onDismiss: () => void }) {
  const { t } = useI18n();
  const paste = kind === 'paste' || kind === 'pasteTouch';
  return (
    <div className="flex items-start gap-3 bg-info/[0.07] px-4 py-2 text-xs sm:px-6" data-testid="intake-hint" data-kind={kind}>
      <Icon name={paste ? 'paste' : 'info'} size={14} className="mt-0.5 text-info" />
      <span className="min-w-0 flex-1 leading-relaxed text-fg">{t(INTAKE_HINT_KEYS[kind])}</span>
      <button
        type="button"
        className="btn-quiet -my-1 h-7 px-1.5"
        onClick={onDismiss}
        aria-label={t('intake.hint.dismiss')}
        title={t('intake.hint.dismiss')}
        data-testid="intake-hint-dismiss"
      >
        <Icon name="x" size={10} />
      </button>
    </div>
  );
}

/** Shown over the manuscript while a file is dragged onto it. */
export function DropOverlay() {
  const { t } = useI18n();
  return (
    <div
      className="pointer-events-none absolute inset-2 z-20 flex items-center justify-center rounded-card border-2 border-dashed border-accent bg-editor/90 p-6"
      data-testid="drop-zone"
      aria-hidden="true"
    >
      <span className="flex max-w-xs items-center gap-3 text-sm font-semibold leading-snug text-accent-strong">
        <GarmentIcon name="folder" size={24} />
        {t('intake.dropHere')}
      </span>
    </div>
  );
}
